import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  CalendarDays,
  QrCode,
  User,
  LogOut,
  Bell,
  Loader2,
  HeartHandshake,
  AlertCircle,
  FileText,
  CalendarCheck,
  MoreHorizontal,
  Menu,
  X,
} from 'lucide-react';
import sidebarLogo from '@assets/ALU Logo.png';
import hamburgerSidebarLogo from '@assets/ALUzon Logo.png';
import defaultAvatar from '@assets/default-avatar.svg';
import '@userStyles/layout.css';
import { fetchUserNotifications, markAllUserNotificationsRead, markUserNotificationRead, deleteUserNotification } from '@/api/users';
import { formatRelativeTime, getCategoryMeta, resolveNotificationPath } from '@/utils/notifications';
import { resolveBackendAssetUrl } from '@/utils/assetUrl';
import { API_BASE_URL } from '@/config';
import Footer from './Footer';

const NAV_ITEMS = [
  { label: 'Dashboard', icon: Home, path: '/dashboard' },
  { label: 'Events', icon: CalendarDays, path: '/news' },
  { label: 'Digital ID', icon: QrCode, path: '/digital-id', isCenter: true },
  { label: 'Venue Booking', icon: CalendarCheck, path: '/venues', activePaths: ['/venues', '/my-venue-bookings'] },
  { label: 'Request & Assistance', icon: HeartHandshake, path: '/request-assistance', activePaths: ['/request-assistance', '/my-requests'] },
  { label: 'Account', icon: User, path: '/account' },
];

const TOPBAR_SUBTITLE_BY_PATH = {
  '/venues': 'Book union facilities for meetings, events, and gatherings.',
  '/request-assistance': 'Submit requests for legal, medical, educational, or emergency support.',
  '/my-requests': 'Submit requests for legal, medical, educational, or emergency support.',
  '/my-venue-bookings': 'View and manage your booked venue events.',
};


export default function AppLayout({ title, user, onLogout, children }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isDashboardPage = pathname === '/dashboard';
  const isAccountPage = pathname === '/account';
  const isVenueDetailPage = /^\/venues\/[^/]+$/.test(pathname);
  const topbarSubtitle = pathname.startsWith('/venues/')
    ? 'Book union facilities for meetings, events, and gatherings.'
    : (TOPBAR_SUBTITLE_BY_PATH[pathname] || user?.company);

  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  const handleLogoutClick = useCallback(() => {
    if (!onLogout) {
      return;
    }
    setLogoutConfirmOpen(true);
  }, [onLogout]);

  const handleConfirmLogout = useCallback(() => {
    if (!onLogout) {
      return;
    }
    setLogoutConfirmOpen(false);
    onLogout();
  }, [onLogout]);

  const handleNavigate = (path, state) => {
    if (pathname !== path || state) {
      navigate(path, state ? { state } : undefined);
    }
  };

  const handleLogoClick = useCallback(() => {
    window.location.assign('/dashboard');
  }, []);

  const userInitials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.trim().toUpperCase() || 'LWU';
  const customAvatar = user?.profilePicture || user?.photo || user?.avatar;
  const resolvedAvatar = resolveBackendAssetUrl(customAvatar, { apiBaseUrl: API_BASE_URL });
  const avatarSource = resolvedAvatar || defaultAvatar;

  const normalizedStatus = typeof user?.isApproved === 'string'
    ? user.isApproved.trim().toLowerCase()
    : user?.isApproved;
  const isRejected = normalizedStatus === 'rejected';
  const isVerified = normalizedStatus === 'approved' || normalizedStatus === 'active'
    || normalizedStatus === true || normalizedStatus === 1 || normalizedStatus === '1';
  
  // Allow access to account (logout) and membership form (details) even if rejected
  const isAllowedPath = ['/membership-form', '/account', '/dashboard'].includes(pathname);

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const notificationsRef = useRef(null);
  const [activeItemMenu, setActiveItemMenu] = useState(null);
  const [pendingItemAction, setPendingItemAction] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const lastScrollY = useRef(0);

  const [notificationState, setNotificationState] = useState({
    items: [],
    unread: 0,
    total: 0,
    loading: false,
    error: null,
  });

  const fetchNotifications = useCallback(async (filter, search) => {
    if (!user?.id) {
      setNotificationState((prev) => ({
        ...prev,
        items: [],
        unread: 0,
        total: 0,
        loading: false,
      }));
      return;
    }

    setNotificationState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const { data } = await fetchUserNotifications(user.id, {
        filter: filter === 'unread' ? 'unread' : 'all',
        search: search || undefined,
        limit: 40,
      });

      const items = Array.isArray(data?.notifications) ? data.notifications : [];

      setNotificationState({
        items,
        unread: Number(data?.meta?.unread ?? 0),
        total: Number(data?.meta?.total ?? items.length),
        loading: false,
        error: null,
      });
      setActiveItemMenu(null);
      setPendingItemAction(null);
    } catch (error) {
      console.error('Failed to load notifications', error);
      setNotificationState((prev) => ({
        ...prev,
        loading: false,
        error: 'Unable to load notifications right now.',
      }));
      setActiveItemMenu(null);
      setPendingItemAction(null);
    }
  }, [user?.id]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchValue(searchInput.trim());
    }, 300);

    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    if (!user?.id) {
      setNotificationState({
        items: [],
        unread: 0,
        total: 0,
        loading: false,
        error: null,
      });
      return;
    }

    fetchNotifications(activeFilter, searchValue);
  }, [user?.id, activeFilter, searchValue, fetchNotifications]);

  useEffect(() => {
    if (!notificationsOpen && !optionsOpen) {
      return undefined;
    }

    const handleClick = (event) => {
      if (!notificationsRef.current || notificationsRef.current.contains(event.target)) {
        return;
      }
      setNotificationsOpen(false);
      setOptionsOpen(false);
      setActiveItemMenu(null);
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [notificationsOpen, optionsOpen]);

  useEffect(() => {
    if (!notificationsOpen && !optionsOpen) {
      return undefined;
    }

    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setNotificationsOpen(false);
        setOptionsOpen(false);
        setActiveItemMenu(null);
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [notificationsOpen, optionsOpen]);

  useEffect(() => {
    setNotificationsOpen(false);
    setOptionsOpen(false);
    setActiveFilter('all');
    setSearchInput('');
    setSearchValue('');
    setActiveItemMenu(null);
  }, [user?.id]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  // Always start at top when navigating to a different page/tab.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  // Hide header on scroll-down, show on scroll-up
  useEffect(() => {
    lastScrollY.current = window.scrollY;

    const handleScroll = () => {
      const currentY = window.scrollY;
      if (currentY < 10) {
        setHeaderHidden(false);
      } else if (currentY > lastScrollY.current + 5) {
        setHeaderHidden(true);
      } else if (currentY < lastScrollY.current - 5) {
        setHeaderHidden(false);
      }
      lastScrollY.current = currentY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!notificationsOpen || activeItemMenu === null) {
      return undefined;
    }

    const handleDocumentClick = (event) => {
      if (!notificationsRef.current) {
        return;
      }
      if (!notificationsRef.current.contains(event.target)) {
        setActiveItemMenu(null);
        return;
      }

      const isTrigger = event.target.closest('[data-notification-menu-trigger]');
      const isPanel = event.target.closest('[data-notification-menu-panel]');

      if (!isTrigger && !isPanel) {
        setActiveItemMenu(null);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, [notificationsOpen, activeItemMenu]);

  const unreadCount = Math.max(0, Number(notificationState.unread) || 0);
  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);
  const hasUnread = unreadCount > 0;
  const totalCount = notificationState.total ?? notificationState.items.length;
  const showingCount = notificationState.items.length;

  const handleToggleNotifications = () => {
    setNotificationsOpen((prev) => {
      const next = !prev;
      if (!prev && user?.id) {
        fetchNotifications(activeFilter, searchValue);
      }
      return next;
    });
    setOptionsOpen(false);
    setActiveItemMenu(null);
  };

  const handleFilterChange = (value) => {
    if (activeFilter !== value) {
      setActiveFilter(value);
    }
  };

  const handleMenuToggle = () => {
    setOptionsOpen((prev) => !prev);
  };

  const handleMarkAllRead = async () => {
    if (!user?.id || unreadCount === 0) {
      setOptionsOpen(false);
      return;
    }

    try {
      await markAllUserNotificationsRead(user.id);
      setNotificationState((prev) => ({
        ...prev,
        unread: 0,
        items: prev.items.map((item) => ({ ...item, isRead: true })),
      }));
    } catch (error) {
      console.error('Unable to mark all notifications as read', error);
    } finally {
      setOptionsOpen(false);
    }
  };

  const handleOpenNotificationsPage = () => {
    setNotificationsOpen(false);
    setOptionsOpen(false);
    setActiveItemMenu(null);
    navigate('/notifications');
  };

  const handleNotificationClick = async (notification) => {
    if (!notification) {
      return;
    }

    setActiveItemMenu(null);

    const navigateTo = resolveNotificationPath(notification.linkUrl);

    if (user?.id && !notification.isRead) {
      try {
        const { data } = await markUserNotificationRead(user.id, notification.id);
        setNotificationState((prev) => ({
          ...prev,
          unread: typeof data?.unread === 'number' ? data.unread : Math.max(0, prev.unread - 1),
          items: prev.items.map((item) => (
            item.id === notification.id ? { ...item, isRead: true } : item
          )),
        }));
      } catch (error) {
        console.error('Unable to mark notification as read', error);
      }
    }

    setNotificationsOpen(false);
    setOptionsOpen(false);

    if (/^https?:\/\//i.test(navigateTo)) {
      window.open(navigateTo, '_blank', 'noopener');
    } else {
      navigate(navigateTo);
    }
  };

  const handleNotificationMenuToggle = (event, notificationId) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveItemMenu((prev) => (prev === notificationId ? null : notificationId));
  };

  const handleMenuMarkRead = async (event, notification) => {
    event.preventDefault();
    event.stopPropagation();

    if (!user?.id || notification.isRead || pendingItemAction === notification.id) {
      if (notification.isRead) {
        setActiveItemMenu(null);
      }
      return;
    }

    try {
      setPendingItemAction(notification.id);
      const { data } = await markUserNotificationRead(user.id, notification.id);
      setNotificationState((prev) => ({
        ...prev,
        unread: typeof data?.unread === 'number' ? data.unread : Math.max(0, prev.unread - 1),
        items: prev.items.map((item) => (
          item.id === notification.id ? { ...item, isRead: true } : item
        )),
      }));
    } catch (error) {
      console.error('Unable to mark notification as read', error);
    } finally {
      setPendingItemAction(null);
      setActiveItemMenu(null);
    }
  };

  const handleMenuDelete = async (event, notification) => {
    event.preventDefault();
    event.stopPropagation();

    if (!user?.id || pendingItemAction === notification.id) {
      return;
    }

    try {
      setPendingItemAction(notification.id);
      const { data } = await deleteUserNotification(user.id, notification.id);
      setNotificationState((prev) => {
        const nextItems = prev.items.filter((item) => item.id !== notification.id);
        const inferredUnread = notification.isRead ? prev.unread : Math.max(0, prev.unread - 1);
        const unread = typeof data?.unread === 'number' ? data.unread : inferredUnread;
        const nextTotal = Math.max(nextItems.length, Math.max(0, prev.total - 1));

        return {
          ...prev,
          unread,
          total: nextTotal,
          items: nextItems,
        };
      });
    } catch (error) {
      console.error('Unable to delete notification', error);
    } finally {
      setPendingItemAction(null);
      setActiveItemMenu(null);
    }
  };

  return (
    <div className={`app-shell${isDashboardPage ? ' app-shell--dashboard' : ''}`}>
      <aside className="sidebar">
        <div className="sidebar__header">
          <div className="sidebar__brand">
            <button
              type="button"
              className="sidebar__logo-button"
              onClick={handleLogoClick}
              aria-label="Go to dashboard"
              title="Go to dashboard"
            >
              <div className="sidebar__logo">
                <img src={sidebarLogo} alt="ALU Logo" />
              </div>
            </button>
          </div>
        </div>
        <nav className="sidebar__nav">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = item.activePaths
              ? item.activePaths.some((p) => pathname === p || pathname.startsWith(p + '/'))
              : pathname === item.path;
            return (
              <button
                key={item.path}
                type="button"
                className={`sidebar__nav-item${isActive ? ' sidebar__nav-item--active' : ''}`}
                onClick={() => handleNavigate(item.path)}
                title={item.label}
                aria-label={item.label}
              >
                <Icon size={18} />
                <span className="sidebar__nav-label">{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="sidebar__footer">
          {user && (
            <div className="sidebar__user">
              <div className="sidebar__avatar">
                <img src={avatarSource} alt={`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || 'Member photo'} />
                {!customAvatar && <span className="sidebar__avatar-initials">{userInitials}</span>}
              </div>
              <div className="sidebar__user-meta">
                <p className="sidebar__user-name">{user.firstName} {user.lastName}</p>
                <span className="sidebar__user-role">{isVerified ? (user.unionPosition ?? 'Member') : '— —'}</span>
              </div>
            </div>
          )}
          {onLogout && (
            <button
              type="button"
              className="sidebar__logout"
              onClick={handleLogoutClick}
              title="Logout"
              aria-label="Logout"
            >
              <LogOut size={18} />
              <span className="sidebar__logout-label">Logout</span>
            </button>
          )}
        </div>
      </aside>

      <main className="main-content">
        {!isAccountPage && !isVenueDetailPage && (
          <header className={`topbar${headerHidden ? ' topbar--hidden' : ''}`}>
            <div className="topbar__left">
              <button
                type="button"
                className="topbar__hamburger"
                aria-label="Open menu"
                onClick={() => setMobileMenuOpen(true)}
              >
                <Menu size={20} />
              </button>
              <div>
                <h1 className="topbar__title">{title}</h1>
                {topbarSubtitle && <p className="topbar__subtitle">{topbarSubtitle}</p>}
              </div>
            </div>
            <div className="topbar__actions">
              <div className="topbar__notifications-wrapper" ref={notificationsRef}>
                <button
                  type="button"
                  className="topbar__notifications"
                  aria-label="Notifications"
                  aria-expanded={notificationsOpen}
                  onClick={handleToggleNotifications}
                >
                  <Bell size={18} />
                  {hasUnread && <span>{badgeLabel}</span>}
                </button>

                {notificationsOpen && (
                  <div className="notifications-popover">
                    <div className="notifications-popover__header">
                      <div>
                        <h3>Notifications</h3>
                        <p>Showing {showingCount} of {totalCount}</p>
                      </div>
                      <div className="notifications-popover__menu">
                        <button
                          type="button"
                          className="notifications-popover__menu-button"
                          aria-label="Notification options"
                          onClick={handleMenuToggle}
                        >
                          <MoreHorizontal size={16} />
                        </button>
                        {optionsOpen && (
                          <div className="notifications-popover__menu-panel">
                            <button
                              type="button"
                              className="notifications-popover__menu-item"
                              onClick={handleMarkAllRead}
                              disabled={unreadCount === 0}
                            >
                              Mark all as read
                            </button>
                            <button
                              type="button"
                              className="notifications-popover__menu-item"
                              onClick={handleOpenNotificationsPage}
                            >
                              Open notifications
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {notificationState.error && (
                      <div className="notifications-popover__error">{notificationState.error}</div>
                    )}

                    <div className="notifications-popover__controls">
                      <div className="notifications-popover__tabs">
                        <button
                          type="button"
                          className={`notifications-popover__tab${activeFilter === 'all' ? ' notifications-popover__tab--active' : ''}`}
                          onClick={() => handleFilterChange('all')}
                        >
                          All
                        </button>
                        <button
                          type="button"
                          className={`notifications-popover__tab${activeFilter === 'unread' ? ' notifications-popover__tab--active' : ''}`}
                          onClick={() => handleFilterChange('unread')}
                        >
                          Unread
                        </button>
                      </div>
                      <div className="notifications-popover__search">
                        <input
                          type="search"
                          value={searchInput}
                          onChange={(event) => setSearchInput(event.target.value)}
                          placeholder="Search notifications"
                          aria-label="Search notifications"
                        />
                      </div>
                    </div>

                    <div className="notifications-popover__content">
                      {notificationState.loading ? (
                        <div className="notifications-popover__loading">
                          <Loader2 size={20} />
                          <span>Loading notifications…</span>
                        </div>
                      ) : showingCount === 0 ? (
                        <div className="notifications-popover__empty">
                          <p>No notifications to show.</p>
                        </div>
                      ) : (
                        <ul className="notifications-popover__list">
                          {notificationState.items.map((item) => {
                            const meta = getCategoryMeta(item.category);
                            const title = item.title?.trim().length ? item.title.trim() : 'Account update';
                            const message = item.message?.trim().length ? item.message.trim() : 'See details in your account.';
                            const avatarInitial = title.charAt(0).toUpperCase();

                            return (
                              <li key={`${item.id}-${item.linkUrl || ''}`}>
                                <div className={`notifications-popover__row${item.isRead ? '' : ' notifications-popover__row--unread'}`}>
                                  <button
                                    type="button"
                                    className="notifications-popover__item"
                                    onClick={() => handleNotificationClick(item)}
                                  >
                                    <div className="notifications-popover__avatar">{avatarInitial || 'N'}</div>
                                    <div className="notifications-popover__item-body">
                                      <div className="notifications-popover__item-head">
                                        <p className="notifications-popover__item-title">{title}</p>
                                      </div>
                                      <p className="notifications-popover__item-message">{message}</p>
                                      <div className="notifications-popover__item-meta">
                                        <span className={`notifications-popover__badge notifications-popover__badge--${meta.tone}`}>{meta.label}</span>
                                        <span className="notifications-popover__item-time notifications-popover__item-time--stacked">{formatRelativeTime(item.createdAt)}</span>
                                      </div>
                                    </div>
                                  </button>
                                  <div className={`notifications-popover__item-actions${activeItemMenu === item.id ? ' notifications-popover__item-actions--active' : ''}`}>
                                    <button
                                      type="button"
                                      className="notifications-popover__item-menu"
                                      aria-label="Notification options"
                                      data-notification-menu-trigger
                                      onClick={(event) => handleNotificationMenuToggle(event, item.id)}
                                    >
                                      <MoreHorizontal size={16} />
                                    </button>
                                    {activeItemMenu === item.id && (
                                      <div className="notifications-popover__item-menu-panel" data-notification-menu-panel>
                                        <button
                                          type="button"
                                          className="notifications-popover__item-menu-item"
                                          onClick={(event) => handleMenuMarkRead(event, item)}
                                          disabled={item.isRead || pendingItemAction === item.id}
                                        >
                                          Mark as read
                                        </button>
                                        <button
                                          type="button"
                                          className="notifications-popover__item-menu-item notifications-popover__item-menu-item--danger"
                                          onClick={(event) => handleMenuDelete(event, item)}
                                          disabled={pendingItemAction === item.id}
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </header>
        )}

        <div className="page-content">
          {isRejected && !isAllowedPath ? (
            <div className="rejection-lockout">
              <div className="rejection-lockout__icon">
                <AlertCircle size={36} />
              </div>
              <h1>Application Rejected</h1>
              <p>
                Your membership application was not approved by our administration team. 
                Please review your application details or contact support for further assistance.
              </p>
              
              {user?.rejectionReason && (
                <div className="rejection-lockout__reason">
                  <div className="rejection-lockout__reason-header">
                    <AlertCircle size={16} />
                    <strong>
                      Rejection Reason
                    </strong>
                  </div>
                  <p>
                    {user.rejectionReason}
                  </p>
                </div>
              )}

              <div className="rejection-lockout__actions">
                <button 
                  type="button"
                  onClick={() => handleNavigate('/membership-form')}
                >
                  <FileText size={18} />
                  Review Application
                </button>
              </div>
            </div>
          ) : (
            children
          )}
        </div>

        <Footer navItems={NAV_ITEMS} onNavigate={handleNavigate} />
      </main>

      {/* Mobile Menu Overlay */}
      <div
        className={`mobile-menu-overlay${mobileMenuOpen ? ' mobile-menu-overlay--open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />
      <div className={`mobile-menu${mobileMenuOpen ? ' mobile-menu--open' : ''}`}>
        <div className="mobile-menu__header">
          <div className="sidebar__brand">
            <button
              type="button"
              className="sidebar__logo-button"
              onClick={handleLogoClick}
              aria-label="Go to dashboard"
              title="Go to dashboard"
            >
              <div className="sidebar__logo">
                <img src={hamburgerSidebarLogo} alt="ALUzon Logo" />
              </div>
            </button>
          </div>
          <button
            type="button"
            className="mobile-menu__close"
            aria-label="Close menu"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X size={20} />
          </button>
        </div>
        <nav className="mobile-menu__nav">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = item.activePaths
              ? item.activePaths.some((p) => pathname === p || pathname.startsWith(p + '/'))
              : pathname === item.path;
            return (
              <button
                key={item.path}
                type="button"
                className={`sidebar__nav-item${isActive ? ' sidebar__nav-item--active' : ''}`}
                onClick={() => {
                  handleNavigate(item.path);
                  setMobileMenuOpen(false);
                }}
              >
                <Icon size={18} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="mobile-menu__footer">
          {user && (
            <div className="sidebar__user">
              <div className="sidebar__avatar">
                <img src={avatarSource} alt={`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || 'Member photo'} />
                {!customAvatar && <span className="sidebar__avatar-initials">{userInitials}</span>}
              </div>
              <div>
                <p className="sidebar__user-name">{user.firstName} {user.lastName}</p>
                <span className="sidebar__user-role">{isVerified ? (user.unionPosition ?? 'Member') : '— —'}</span>
              </div>
            </div>
          )}
          {onLogout && (
            <button type="button" className="sidebar__logout" onClick={handleLogoutClick}>
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          )}
        </div>
      </div>

      {logoutConfirmOpen ? (
        <div className="layout-confirm" role="dialog" aria-modal="true" aria-label="Confirm logout">
          <button
            type="button"
            className="layout-confirm__backdrop"
            aria-label="Close logout confirmation"
            onClick={() => setLogoutConfirmOpen(false)}
          />
          <div className="layout-confirm__panel">
            <h3>Confirm Logout</h3>
            <p>Are you sure you want to log out?</p>
            <div className="layout-confirm__actions">
              <button
                type="button"
                className="layout-confirm__button layout-confirm__button--ghost"
                onClick={() => setLogoutConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="layout-confirm__button layout-confirm__button--danger"
                onClick={handleConfirmLogout}
              >
                Log out
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

AppLayout.propTypes = {
  title: PropTypes.string.isRequired,
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    unionPosition: PropTypes.string,
    unionAffiliation: PropTypes.string,
    company: PropTypes.string,
    membershipDate: PropTypes.string,
    profilePicture: PropTypes.string,
    photo: PropTypes.string,
    avatar: PropTypes.string,
  }),
  onLogout: PropTypes.func,
  children: PropTypes.node.isRequired,
};

AppLayout.defaultProps = {
  user: null,
  onLogout: null,
};
